import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const SECTION_ID = 'cart-drawer-section';

function rootUrl() {
  return window.Shopify?.routes?.root ?? '/';
}

/**
 * Adds a single-variant product to the cart from the 'Add a little extra' carousel,
 * then refreshes the cart drawer using the theme's own section rendering.
 * @param {HTMLButtonElement} button
 */
async function addToCart(button) {
  const variantId = Number(button.dataset.variantId);
  if (!variantId || button.disabled) return;

  const originalLabel = button.textContent;
  button.disabled = true;
  button.textContent = 'Adding...';

  // Tell the rest of the theme (cart bubble, drawer, analytics) that a line is being added.
  let deferred = null;
  try {
    deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ id: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );
  } catch (error) {
    deferred = null;
  }

  try {
    const addResponse = await fetch(`${rootUrl()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: SECTION_ID,
        sections_url: window.location.pathname,
      }),
    });
    const added = await addResponse.json();
    if (!addResponse.ok || added.status || added.errors) {
      throw new Error(added.description || added.message || 'Could not add item');
    }

    const cartResponse = await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await cartResponse.json();
    cart.sections = added.sections;

    let handled = false;
    if (deferred) {
      try {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            sections: added.sections,
            items: cart.items,
            itemCount: cart.item_count,
            source: 'cart-upsell',
            didError: false,
          },
        });
        handled = true;
      } catch (error) {
        handled = false;
      }
    }

    if (!handled && added.sections?.[SECTION_ID]) {
      await morphSection(SECTION_ID, added.sections[SECTION_ID], { mode: 'hydration' });
    }

    // If the drawer did not re-render for any reason, make the button usable again.
    setTimeout(() => {
      if (button.isConnected) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
    }, 3000);
  } catch (error) {
    console.warn('[cart-upsell] Add to cart failed:', error);
    deferred?.reject(error);
    try {
      button.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Failed to add item', code: 'INVALID' }));
    } catch (dispatchError) {
      // Ignore: the button state below is enough feedback.
    }
    button.disabled = false;
    button.textContent = originalLabel;
  }
}

if (!window.__cartUpsellBound) {
  window.__cartUpsellBound = true;
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-upsell-add]') : null;
    if (button instanceof HTMLButtonElement) {
      event.preventDefault();
      addToCart(button);
    }
  });
}
